import React from 'react'
import { navigate } from 'gatsby'
import { modal, ModalParams } from '@codeleap/modals'

import { Image, View, ActionIcon, Text, Button } from '@/components'
import { Celebration2 } from '@/app/assets/images'
import { variantProvider } from '@/app'
import { useIsMobile } from '@/utils'
import { APIClient, analytics } from '@/services'
import { AppStatus } from '@/redux'

type ReviewOrgSuccessModalProps = {
  sourceFrom?: 'after_review' | 'unknown'
}

const Content = (props: ModalParams<ReviewOrgSuccessModalProps>) => {
  const { toggle, visible } = props
  const profile = APIClient.Session.useProfile()
  const isMobile = useIsMobile()

  const orgId = profile?.data?.organization?.id

  const close = () => toggle()

  const onAddStory = () => {
    close()
    analytics.track('story_add_start', { source_from: 'after_review' })
    AppStatus.setModal('createStory')
  }

  const onViewResults = () => {
    close()

    AppStatus.set('loading')
    setTimeout(() => {
      AppStatus.set('idle')
      if (orgId) {
        navigate(`/organisations/${orgId}/`)
      }
    }, 800)
  }

  const Header = () => {
    return (
      <View variants={['column', 'relative', 'fullWidth']} style={styles.header}>
        <ActionIcon
          debugName='successPulse:close'
          name='x'
          variants={['minimal', 'primary3', 'absolute', 'iconSize:3']}
          style={styles.close}
          onPress={close}
        />

        {isMobile ? (
          <View variants={['column', 'center']} style={styles.mobileTitle}>
            <Text variants={['h1']} text='Thank You!' />
            <Text variants={['p1']} text='Thanks for your valuable input.' />
          </View>
        ) : null}

        <Image source={Celebration2} objectFit='contain' style={styles.image} />
      </View>
    )
  }

  return (
    <View style={styles.wrapper}>
      <Header />

      {!isMobile ? (
        <View variants={['column', 'center']} style={styles.desktopTitle}>
          <Text variants={['h1']} text='Thank You!' />
          <Text variants={['p1']} text='Thanks for your valuable input.' />
        </View>
      ) : null}

      <View variants={['column', 'gap:2']} style={styles.actions}>
        <Button
          debugName='successPulse:story'
          text='Add a climate action success story'
          onPress={onAddStory}
          variants={['pill', 'large']}
        />

        <Button
          debugName='successPulse:results'
          text='View my organisation'
          onPress={onViewResults}
          variants={['pill', 'large', 'outline']}
          disabled={!orgId}
        />
      </View>
    </View>
  )
}

const MODAL_WIDTH = 400

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      ...theme.presets.column,
      backgroundColor: theme.colors.primary1,
      ...theme.spacing.padding(4),
      paddingTop: 0,

      [theme.media.down('small')]: {
        ...theme.spacing.padding(2),
        paddingTop: 0,
        width: '100%',
      },
    },

    box: {
      // @ts-ignore
      borderRadius: theme.borderRadius.large,
      width: MODAL_WIDTH,
      maxWidth: `calc(100vw - ${theme.spacing.value(4)}px)`,
      maxHeight: '85dvh',

      ...theme.spacing.padding(0),
      backgroundColor: theme.colors.primary1,

      [theme.media.down('small')]: {
        width: `calc(100vw - ${theme.spacing.value(4)}px)`,
        maxWidth: `calc(100vw - ${theme.spacing.value(4)}px)`,
        maxHeight: '85dvh',
        borderRadius: theme.borderRadius.large,
      },
    },
    body: {
      width: '100%',
      backgroundColor: theme.colors.primary1,
      maxHeight: '90dvh',
      overflowY: 'auto',
    },
    wrapperOuter: {
      maxHeight: '85dvh',

      [theme.media.down('small')]: {
        maxHeight: '85dvh',
      },
    },
    header: {
      ...theme.presets.column,
      ...theme.presets.fullWidth,
      backgroundColor: theme.colors.primary1,
      position: 'relative',
    },
    close: {
      top: theme.spacing.value(2),
      right: theme.spacing.value(2),

      [theme.media.down('small')]: {
        top: theme.spacing.value(2),
        right: theme.spacing.value(2),
      },
    },
    image: {
      height: 400,

      [theme.media.down('small')]: {
        height: 320,
      },
    },
    mobileTitle: {
      ...theme.spacing.marginTop(4),
      ...theme.spacing.marginBottom(2),
    },
    desktopTitle: {
      ...theme.spacing.marginTop(4),
    },
    actions: {
      ...theme.spacing.marginTop(4),

      [theme.media.down('small')]: {
        ...theme.spacing.marginTop(2),
      },
    },
  }),
  true,
)

export const ReviewOrgSuccessModal = modal<ReviewOrgSuccessModalProps>()
  .content(p => <Content {...p} />)
  .props({
    variants: ['centered'],
    showClose: false,
    styles: {
      wrapper: styles.wrapperOuter,
      box: styles.box,
      body: styles.body,
    },
  })
